import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../../components/base/LanguageSwitcher";
import ideaLight from "@/assets/images/idea-light.png";
import webDashboardImg from "@/assets/images/web-frontend-dev.png";
import iotControllerImg from "@/assets/images/iot-dev0.png";
import automationModuleImg from "@/assets/images/automation-module.png";
import sensorSystemImg from "@/assets/images/sensor-system.png";
import hardwarePrototypeImg from "@/assets/images/hardware-prototype.png";
import myProfileImg from "@/assets/images/me.png";

const BrainPlayPage = () => {
  const { t } = useTranslation();

  // Handle initial hash navigation and hash changes
  useEffect(() => {
    const scrollToHash = () => {
      const hash = window.location.hash.slice(1); // Remove #
      if (hash) {
        const element = document.getElementById(hash);
        if (element) {
          setTimeout(() => {
            element.scrollIntoView({ behavior: "smooth" });
          }, 100);
        }
      }
    };

    // Scroll on initial load
    scrollToHash();

    // Listen for hash changes
    window.addEventListener("hashchange", scrollToHash);
    return () => window.removeEventListener("hashchange", scrollToHash);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      // Update URL hash
      window.history.pushState(null, "", `#${sectionId}`);
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    try {
      const response = await fetch(
        "https://readdy.ai/api/form/d4cppvqels5gugpjmg10",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: new URLSearchParams(formData as any).toString(),
        }
      );

      if (response.ok) {
        alert("메시지가 성공적으로 전송되었습니다!");
        (e.target as HTMLFormElement).reset();
      } else {
        alert("메시지 전송에 실패했습니다. 다시 시도해주세요.");
      }
    } catch (error) {
      alert("메시지 전송 중 오류가 발생했습니다.");
    }
  };

  const portfolioItems = [
    {
      title: t("webDashboards"),
      image: webDashboardImg,
    },
    {
      title: t("iotControllers"),
      image: iotControllerImg,
    },
    {
      title: t("automationModules"),
      image: automationModuleImg,
    },
    {
      title: t("sensorSystems"),
      image: sensorSystemImg,
    },
    {
      title: t("hardwarePrototypes"),
      image: hardwarePrototypeImg,
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/90 backdrop-blur-md z-50 border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            {/* <img
              src="https://readdy.ai/api/search-image?query=Simple%20minimalist%20lightbulb%20icon%2C%20clean%20line%20art%20design%2C%20thinking%20idea%20symbol%2C%20monochrome%20black%20outline%2C%20creative%20concept%20logo%2C%20geometric%20minimal%20style%2C%20professional%20branding&width=40&height=40&seq=nav-logo-simple&orientation=squarish"
              alt="Brain Play Logo"
              className="w-10 h-10"
            /> */}
            <img src={ideaLight} alt="Idea Light" className="w-10 h-10" />
            <div className="font-bold text-xl text-gray-900">BRAIN PLAY</div>
            <div className="hidden md:flex items-center gap-8">
              <button
                onClick={() => scrollToSection("about")}
                className="text-gray-600 hover:text-blue-600 transition-colors"
              >
                {t("about")}
              </button>
              <button
                onClick={() => scrollToSection("skills")}
                className="text-gray-600 hover:text-blue-600 transition-colors"
              >
                {t("skills")}
              </button>
              <button
                onClick={() => scrollToSection("services")}
                className="text-gray-600 hover:text-blue-600 transition-colors"
              >
                {t("services")}
              </button>
              <button
                onClick={() => scrollToSection("portfolio")}
                className="text-gray-600 hover:text-blue-600 transition-colors"
              >
                {t("portfolio")}
              </button>
              <button
                onClick={() => scrollToSection("contact")}
                className="text-gray-600 hover:text-blue-600 transition-colors"
              >
                {t("contact")}
              </button>
              <LanguageSwitcher />
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-24 pb-20 px-4">
        <div className="max-w-6xl mx-auto text-center">
          {/* <div className="flex justify-center mb-8">
            <img
              src="https://readdy.ai/api/search-image?query=Minimalist%20lightbulb%20illustration%2C%20simple%20clean%20design%2C%20thinking%20and%20creativity%20symbol%2C%20elegant%20line%20art%2C%20monochrome%20style%2C%20idea%20concept%20icon%2C%20professional%20minimal%20branding%2C%20geometric%20simplicity&width=120&height=120&seq=hero-logo-simple&orientation=squarish"
              alt="Brain Play Logo"
              className="w-20 h-20"
            />
          </div> */}
          <h1
            className="text-6xl md:text-8xl font-bold text-gray-900 mb-6"
            style={{ fontFamily: '"Pacifico", serif' }}
          >
            {t("title")}
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 mb-12">
            {t("subtitle")}
          </p>
          <div className="w-full h-64 rounded-2xl overflow-hidden mb-12">
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20tech%20workspace%20with%20circuit%20board%20patterns%2C%20clean%20desk%20setup%2C%20multiple%20monitors%20displaying%20code%20and%20hardware%20schematics%2C%20subtle%20blue%20lighting%2C%20professional%20engineering%20environment%2C%20high-tech%20aesthetic%2C%20clean%20and%20organized%2C%20futuristic%20design%20elements&width=1200&height=400&seq=hero-tech&orientation=landscape"
              alt="Tech workspace"
              className="w-full h-full object-cover object-center"
            />
          </div>
        </div>
      </section>

      {/* About Section */}
      {/* https://readdy.ai/api/search-image?query=Professional%20Korean%20software%20engineer%20portrait%2C%20clean%20background%2C%20modern%20business%20casual%20attire%2C%20confident%20expression%2C%20tech%20professional%20headshot%2C%20minimal%20lighting%2C%20high%20quality%20professional%20photo&width=200&height=200&seq=profile-photo&orientation=squarish */}
      <section id="about" className="py-20 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
            {t("aboutTitle")}
          </h2>
          <div className="bg-white rounded-2xl p-8 shadow-sm">
            <div className="flex flex-col md:flex-row items-center gap-8">
              {/* <div className="w-32 h-32 rounded-full overflow-hidden">
                <img
                  src={myProfileImg}
                  alt="Profile"
                  className="w-full h-full object-cover object-top"
                />
              </div> */}
              <div className="flex-1 text-center md:text-left">
                {/* <h3 className="text-2xl font-bold text-gray-900 mb-2">{t('name')}</h3> */}
                <p className="text-lg text-blue-600 mb-4">{t("role")}</p>
                <p className="text-gray-600 leading-relaxed">{t("bio")}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section id="skills" className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
            {t("skillsTitle")}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: t("webFrontend"),
                skills: ["JavaScript", "TypeScript", "Vue", "React"],
                icon: "ri-code-s-slash-line",
              },
              {
                title: t("backend"),
                skills: [
                  "Node.js",
                  "Python",
                  "C#",
                  "Linux server",
                  "REST APIs",
                  "Java",
                  "Spring Boot",
                ],
                icon: "ri-server-line",
              },
              {
                title: t("programming"),
                skills: ["C", "C++", "C#", "Python", "Shell"],
                icon: "ri-terminal-line",
              },
              {
                title: t("embedded"),
                skills: [
                  "Embedded Linux",
                  "Arduino",
                  "ESP32",
                  "Raspberry Pi",
                  "AVR",
                  "MSP430",
                  "PIC",
                ],
                icon: "ri-cpu-line",
              },
              {
                title: t("systems"),
                skills: [
                  "Sensors",
                  "Control modules",
                  "Automation",
                  "Network design",
                ],
                icon: "ri-settings-3-line",
              },
              // { title: t('platforms'), skills: ['Embedded Linux', 'Microcontrollers'], icon: 'ri-computer-line' }
            ].map((category, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <i className={`${category.icon} text-blue-600 text-xl`}></i>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {category.title}
                  </h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  {category.skills.map((skill, skillIndex) => (
                    <span
                      key={skillIndex}
                      className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
            {t("servicesTitle")}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {portfolioItems.map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-all hover:-translate-y-1"
              >
                {/* 이미지 박스 */}
                <div className="w-full h-40 rounded-xl overflow-hidden flex items-center justify-center bg-gray-100 mb-4">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover object-center"
                  />
                </div>

                {/* 텍스트 */}
                <h3 className="text-lg font-semibold text-gray-900 mb-3 text-center">
                  {item.title}
                </h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Section */}
      <section id="portfolio" className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
            {t("portfolioTitle")}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* {[
              { title: t('webDashboards'), image: 'Modern web dashboard interface with charts and analytics, clean UI design, blue and white color scheme, professional business dashboard, data visualization, responsive design' },
              { title: t('iotControllers'), image: 'IoT controller device with sensors and microcontroller, electronic components, Arduino or ESP32 board, connected sensors, modern IoT hardware setup' },
              { title: t('automationModules'), image: 'Raspberry Pi automation module with connected sensors and wires, home automation setup, electronic project, clean technical photography' },
              { title: t('sensorSystems'), image: 'Embedded sensor system with microcontroller and various sensors, electronic prototyping board, technical hardware project, engineering setup' },
              { title: t('hardwarePrototypes'), image: 'Custom hardware prototype with PCB board and electronic components, engineering prototype, technical innovation, modern electronics design' }
            ].map((project, index) => (
              <div key={index} className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                <div className="h-48 overflow-hidden">
                  <img
                    src={`https://readdy.ai/api/search-image?query=$%7Bproject.image%7D&width=400&height=300&seq=portfolio-${index}&orientation=landscape`}
                    alt={project.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900">{project.title}</h3>
                </div>
              </div>
            ))} */}
            {portfolioItems.map((project, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="h-48 overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {project.title}
                  </h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
            {t("contactTitle")}
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div className="bg-white rounded-2xl p-8 shadow-sm">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                {t("name")}
              </h3>
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <i className="ri-mail-line text-blue-600"></i>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t("email")}</p>
                    <p className="text-gray-900">simx6458@gmail.com</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <i className="ri-global-line text-blue-600"></i>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t("website")}</p>
                    <p className="text-gray-900">https://brainplay.pages.dev</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white rounded-2xl p-8 shadow-sm">
              <h3 className="text-xl font-semibold text-gray-900 mb-6">
                {t("contactForm")}
              </h3>
              <form
                onSubmit={handleContactSubmit}
                data-readdy-form
                id="contact-form"
                className="space-y-4"
              >
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("nameField")}
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("emailField")}
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("messageField")}
                  </label>
                  <textarea
                    name="message"
                    rows={4}
                    required
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  ></textarea>
                </div>
                <button
                  type="submit"
                  className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  {t("sendMessage")}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 bg-gray-900 text-white">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-gray-400">
            © 2024 Brain Play. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default BrainPlayPage;
